import {
  ApartmentOutlined,
  AuditOutlined,
  CarOutlined,
  ClusterOutlined,
  HeartOutlined,
  HomeOutlined,
  NotificationOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  ToolOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import type { ItemType } from "antd/es/menu/interface";
import type { ReactNode } from "react";

export type SectorKey = "wheels" | "health" | "homes";

export type SectorDefinition = {
  key: SectorKey;
  label: string;
  shortLabel: string;
  icon: ReactNode;
  basePath: string;
  enabled: boolean;
  accent: string;
  summary: string;
};

export type PageMeta = {
  key: string;
  label: string;
  description: string;
  href: string;
  section: string;
};

export type WorkspaceItem = {
  title: string;
  owner: string;
  status: string;
  summary: string;
};

export const sectors: SectorDefinition[] = [
  {
    key: "wheels",
    label: "Wheels",
    shortLabel: "Wheels",
    icon: <CarOutlined />,
    basePath: "/wheels/dashboard",
    enabled: true,
    accent: "#00e5ff",
    summary: "Marketplace operations for dealers, inventory, auctions, and buyers.",
  },
  {
    key: "health",
    label: "Health",
    shortLabel: "Health",
    icon: <HeartOutlined />,
    basePath: "/health",
    enabled: false,
    accent: "#49c27d",
    summary: "Clinic, provider, and care operations across the health vertical.",
  },
  {
    key: "homes",
    label: "Homes",
    shortLabel: "Homes",
    icon: <HomeOutlined />,
    basePath: "/homes",
    enabled: false,
    accent: "#f59f3a",
    summary: "Property, broker, and resident operations across the homes vertical.",
  },
];

export const pages: PageMeta[] = [
  { key: "overview", label: "Overview", description: "Cross-sector health, risk, and platform activity", href: "/overview", section: "Platform" },
  { key: "workspace", label: "Workspace", description: "Internal initiatives, staffing, and execution tracking", href: "/workspace", section: "Platform" },
  { key: "governance", label: "Governance", description: "Approvals, controls, and policy monitoring", href: "/governance", section: "Platform" },
  { key: "wheels-dashboard", label: "Dashboard", description: "Marketplace performance and live activity", href: "/wheels/dashboard", section: "Wheels" },
  { key: "wheels-operations", label: "Operations", description: "Operational queues, exceptions, and SLA tracking", href: "/wheels/operations", section: "Wheels" },
  { key: "wheels-dealers", label: "Dealers", description: "Dealer network management and compliance", href: "/wheels/dealers", section: "Wheels" },
  { key: "wheels-catalog", label: "Catalog", description: "Listings, vehicles, auctions, and moderation review", href: "/wheels/catalog", section: "Wheels" },
  { key: "wheels-customers", label: "Customers", description: "Buyer support, disputes, and case handling", href: "/wheels/customers", section: "Wheels" },
  { key: "health-home", label: "Health", description: "Health admin module is not configured yet", href: "/health", section: "Other Sectors" },
  { key: "homes-home", label: "Homes", description: "Homes admin module is not configured yet", href: "/homes", section: "Other Sectors" },
];

export const sidebarItems: ItemType[] = [
  {
    type: "group",
    label: "Platform",
    children: [
      { key: "/overview", icon: <NotificationOutlined />, label: "Overview" },
      { key: "/workspace", icon: <ApartmentOutlined />, label: "Workspace" },
      { key: "/governance", icon: <AuditOutlined />, label: "Governance" },
    ],
  },
  {
    type: "group",
    label: "Wheels",
    children: [
      { key: "/wheels/dashboard", icon: <CarOutlined />, label: "Dashboard" },
      { key: "/wheels/operations", icon: <ToolOutlined />, label: "Operations" },
      { key: "/wheels/dealers", icon: <TeamOutlined />, label: "Dealers" },
      { key: "/wheels/catalog", icon: <SafetyCertificateOutlined />, label: "Catalog" },
      { key: "/wheels/customers", icon: <UserSwitchOutlined />, label: "Customers" },
    ],
  },
  {
    type: "group",
    label: "Other Sectors",
    children: [
      { key: "/health", icon: <ClusterOutlined />, label: "Health", disabled: true },
      { key: "/homes", icon: <ClusterOutlined />, label: "Homes", disabled: true },
    ],
  },
];

export const workspaceItems: WorkspaceItem[] = [
  { title: "Policy rollout", owner: "People Ops", status: "Ready for review", summary: "Cross-sector access policy and approval flow for new internal teams." },
  { title: "SLA calibration", owner: "Operations Excellence", status: "In progress", summary: "Normalize escalation windows across marketplace, care, and property teams." },
  { title: "Support taxonomy", owner: "Customer Success", status: "Blocked", summary: "Unify issue categories so reporting works across every sector." },
];

export function getPageMeta(pathname: string): PageMeta {
  return pages.find((page) => page.href === pathname) ?? pages[0];
}

export function getSector(sectorParam?: string): SectorDefinition {
  return sectors.find((sector) => sector.key === sectorParam) ?? sectors[0];
}
